import React, { useEffect, useState } from "react";
import { timePercentage } from "../utils/timeUtils";
import { Alert, Card, CardHeader, CardContent, Grid, Typography, LinearProgress, IconButton, TextField, Box, Select, MenuItem, FormControl, InputLabel, Chip, Divider, Button, InputAdornment, Tooltip } from "@mui/material"
import styled from "@emotion/styled"
import { Edit, Check, Close, Add, Delete, LockOutlined, WarningAmber } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useHasPermission } from "../utils/permissionUtils";
import { useIsProjectLocked } from "../utils/lockUtils";
import { updateProject } from "../redux/slices/project";
import { updateGroup } from "../redux/ducks/group";
import { clearFixedPricePayments } from "../redux/ducks/fixedPricePayments";
import { triggerRefresh } from "../redux/slices/refreshTriggers";
import { showAlert } from "../redux/slices/alerts";
import AppDatePicker from "./AppDatePicker";
import { formatDate as formatDay } from "../utils/dateUtils";

const EDITABLE_PAYMENT_STATUSES = new Set(['Pending', 'Due']);
const isPaymentLocked = (payment) => !EDITABLE_PAYMENT_STATUSES.has(payment?.status);

const InfoCard = styled(Card)(({ theme }) => ({
    gridColumn: 'span 3',
    [theme.breakpoints.up('sm')]: {
        gridColumn: 'span 3',
    }
}));

const CardTitleText = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: theme.typography.h5.fontSize,
}));

const ContentGridSpaced = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    paddingBottom: theme.spacing(1),
    alignItems: 'center',
}));

const SummaryColumn = styled(Box)(({ theme }) => ({
    paddingRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
        paddingRight: 0,
        paddingBottom: theme.spacing(2),
    },
}));

const DetailsColumn = styled(Box)(({ theme }) => ({
    paddingLeft: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
        paddingLeft: 0,
    },
}));

const DetailsBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: theme.spacing(2),
}));

const DetailsDivider = styled(Divider)(({ theme }) => ({
    marginBottom: theme.spacing(1),
}));

const PaymentItem = styled(Box)(({ theme }) => ({
    paddingBottom: theme.spacing(2),
    '&:not(:last-child)': {
        marginBottom: theme.spacing(2),
        paddingBottom: theme.spacing(2),
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
}));

const ProgressBarCaption = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.disabled,
    paddingBottom: theme.spacing(2),
}));

const CardHeaderContent = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
}));

const ButtonGroup = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(1),
}));

const PaymentAmountContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
}));

const PaymentDetailsContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    alignItems: 'center',
    marginTop: theme.spacing(1),
}));

const PaymentProgressContainer = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(1),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
    width: 120,
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
    minWidth: 120,
}));

const StyledDateTextField = styled(Box)(({ theme }) => ({
    minWidth: 150,
}));

const StyledLinearProgress = styled(LinearProgress)(({ theme }) => ({
    marginTop: theme.spacing(0.5),
}));

const LockIndicator = styled(Box)(({ theme }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    color: theme.palette.text.disabled,
}));

const ProjectPayments = ({ type }) => {
    const dispatch = useDispatch();
    const hasPermission = useHasPermission();
    const project = useSelector(state => state.project.currentProject);
    const isLocked = useIsProjectLocked(project);
    const payments = type === 'Group' ? project?.group?.payments || [] : project?.payments || [];
    const [isEditing, setIsEditing] = useState(false);
    const [localPayments, setLocalPayments] = useState([]);
    const [editingPaymentIndex, setEditingPaymentIndex] = useState(null);
    const canSetBillingType = hasPermission('setProjectBilling');
    const canEditCompleted = hasPermission('editCompletedProjects');
    const canRunBilling = hasPermission('canRunBilling');

    const billingType = type === 'Group' ? project?.group?.billingTerms?.type : project?.billingTerms?.type;
    const isOrphan = billingType !== 'Fixed Price' && payments.length > 0;
    const parentId = type === 'Group' ? project?.group?._id : project?._id;

    const [isClearing, setIsClearing] = useState(false);
    const handleClearAll = () => {
        if (!parentId) return;
        setIsClearing(true);
        dispatch(clearFixedPricePayments(type, parentId, (ok) => {
            setIsClearing(false);
            if (ok) dispatch(triggerRefresh({ triggerName: 'project' }));
        }));
    };

    const currentPayments = isEditing ? localPayments : payments;
    const totalPayment = currentPayments.reduce((acc, payment) => acc + (parseFloat(payment.value) || 0), 0);
    const totalPaid = currentPayments
        .filter(payment => payment.status === 'Paid')
        .reduce((acc, payment) => acc + (parseFloat(payment.value) || 0), 0);
    const totalDue = currentPayments
        .filter(payment => payment.status === 'Due')
        .reduce((acc, payment) => acc + (parseFloat(payment.value) || 0), 0);
    const totalRemaining = totalPayment - totalPaid;

    useEffect(() => {
        if (project) {
            setLocalPayments(payments);
        }
    }, [project, payments]);

    const handleEdit = () => {
        setIsEditing(true);
        setEditingPaymentIndex(null);
    };

    const handleCancel = () => {
        setIsEditing(false);
        setEditingPaymentIndex(null);
        setLocalPayments(payments);
    };

    const handlePaymentChange = (index, field, value) => {
        setLocalPayments(prev => {
            const updated = [...prev];
            const currentPayment = updated[index];
            
            // Validate value - cannot be 0 or negative
            if (field === 'value') {
                const numValue = parseFloat(value);
                if (isNaN(numValue) || numValue <= 0) {
                    return prev; // Don't update if invalid
                }
            }
            
            // Prevent setting Paid status for new payments
            if (field === 'status' && value === 'Paid') {
                const isNew = index >= payments.length || !payments.find(p => p.paymentNumber === currentPayment.paymentNumber);
                if (isNew) {
                    return prev; // Don't allow Paid status for new payments
                }
            }
            
            updated[index] = { ...updated[index], [field]: value };
            // Recalculate percentage if value changed
            if (field === 'value') {
                const newTotal = updated.reduce((acc, p) => acc + (parseFloat(p.value) || 0), 0);
                if (newTotal > 0) {
                    updated[index].percentage = ((parseFloat(value) || 0) / newTotal) * 100;
                } else {
                    updated[index].percentage = 0;
                }
            }
            return updated;
        });
    };

    const handleStartEditPayment = (index) => {
        if (isPaymentLocked(localPayments[index])) return;
        setEditingPaymentIndex(index);
    };

    const handleCancelEditPayment = () => {
        setEditingPaymentIndex(null);
        setLocalPayments(payments);
    };

    const handleSavePayment = (index) => {
        setEditingPaymentIndex(null);
    };

    const isNewPayment = (payment, index) => {
        // A payment is new if it doesn't exist in the original payments array
        // Check by comparing if the index is beyond the original payments length
        // or if the paymentNumber doesn't exist in original payments
        if (index >= payments.length) return true;
        const originalPayment = payments.find(p => p.paymentNumber === payment.paymentNumber);
        return !originalPayment;
    };

    const handleAddPayment = () => {
        setLocalPayments(prev => {
            const maxPaymentNumber = prev.length > 0 
                ? Math.max(...prev.map(p => p.paymentNumber || 0))
                : 0;
            const newPayment = {
                paymentNumber: maxPaymentNumber + 1,
                status: 'Pending',
                percentage: 0,
                value: '',
                date: null
            };
            setEditingPaymentIndex(prev.length);
            return [...prev, newPayment];
        });
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Paid':
                return 'success';
            case 'Due':
                return 'warning';
            case 'Open':
                return 'info';
            case 'Overdue':
                return 'error';
            case 'Pending':
            default:
                return 'default';
        }
    };

    const formatDate = (date) => formatDay(date) || 'Not set';

    const handleSave = () => {
        if (isLocked) return;

        const originalByNumber = new Map(payments.map((p) => [p.paymentNumber, p]));

        const invalidPayments = localPayments.filter((p) => !isPaymentLocked(p) && (!p.value || parseFloat(p.value) <= 0));
        if (invalidPayments.length > 0) {
            dispatch(showAlert({ severity: 'error', message: 'All payments must have a value greater than $0.00' }));
            return;
        }

        const resolved = localPayments.map((p) => {
            const original = originalByNumber.get(p.paymentNumber);
            const locked = original ? isPaymentLocked(original) : false;
            const source = locked ? original : p;
            const value = locked ? (parseFloat(original.value) || 0) : (parseFloat(p.value) || 0);
            return { source, locked, value, p, original };
        });

        const total = resolved.reduce((acc, r) => acc + r.value, 0);

        const outgoingPayments = resolved.map(({ source, locked, value, p, original }) => ({
            ...(source._id ? { _id: source._id } : {}),
            paymentNumber: source.paymentNumber,
            status: source.status || 'Pending',
            value,
            percentage: total > 0 ? (value / total) * 100 : 0,
            date: locked ? (original.date ?? null) : (p.date && p.date !== '' ? new Date(p.date) : null),
            ...(source.relativeDate ? { relativeDate: source.relativeDate } : {}),
        }));

        if (type === 'Group') {
            // Update group payments
            const groupId = project?.group?._id;
            if (!groupId) {
                dispatch(showAlert({ severity: 'error', message: 'Group ID not found' }));
                return;
            }
            
            const groupData = {
                payments: outgoingPayments,
                totalPayment: total
            };
            
            dispatch(updateGroup(groupId, groupData, (response) => {
                if (response && response.group) {
                    dispatch(showAlert({ severity: 'success', message: 'Group payments updated successfully' }));
                    dispatch(triggerRefresh({ triggerName: 'project' }));
                    setIsEditing(false);
                    setEditingPaymentIndex(null);
                } else if (response) {
                    dispatch(showAlert({ severity: 'error', message: 'Failed to update group payments' }));
                }
            }));
        } else {
            // Update project payments
            const projectData = {
                payments: outgoingPayments,
                totalPayment: total
            };
            dispatch(updateProject({ projectId: project._id, projectData }));
            setIsEditing(false);
            setEditingPaymentIndex(null);
        }
    };

    const showPayments = isEditing || (localPayments.length > 0);

    return (
        <InfoCard>
            <CardHeader
                title={
                    <CardHeaderContent>
                        <CardTitleText>{type} Payments</CardTitleText>
                        {(project?.completedDate === null || canEditCompleted) && canSetBillingType && !isLocked && (
                            <ButtonGroup>
                                {!isEditing ? (
                                    <IconButton onClick={handleEdit}>
                                        <Edit />
                                    </IconButton>
                                ) : (
                                    <>
                                        <IconButton onClick={handleSave}>
                                            <Check />
                                        </IconButton>
                                        <IconButton onClick={handleCancel}>
                                            <Close />
                                        </IconButton>
                                    </>
                                )}
                            </ButtonGroup>
                        )}
                    </CardHeaderContent>
                }
            />
            <CardContent>
                {isOrphan && (
                    <Alert
                        severity="warning"
                        icon={<WarningAmber />}
                        sx={{ mb: 2 }}
                        action={canRunBilling && (
                            <Button
                                color="warning"
                                size="small"
                                startIcon={<Delete />}
                                onClick={handleClearAll}
                                disabled={isClearing || payments.some(p => p.status === 'Paid')}
                            >
                                {isClearing ? 'Clearing...' : 'Clear all'}
                            </Button>
                        )}
                    >
                        This {type.toLowerCase()} is {billingType || 'non-Fixed Price'} but has Fixed Price payment milestones attached. These will cause billing errors.
                    </Alert>
                )}
                {showPayments ? (
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <SummaryColumn>
                                <Typography variant="h6" gutterBottom>
                                    Summary
                                </Typography>
                                <ContentGridSpaced>
                                    <Typography fontWeight="medium">Total Payment</Typography>
                                    <Typography fontWeight="medium">$ {totalPayment.toFixed(2)}</Typography>
                                </ContentGridSpaced>
                                <DetailsDivider />
                                <ContentGridSpaced>
                                    <Typography>Total Paid</Typography>
                                    <Typography fontWeight="medium">$ {totalPaid.toFixed(2)}</Typography>
                                </ContentGridSpaced>
                                <ContentGridSpaced>
                                    <Typography>Total Due</Typography>
                                    <Typography fontWeight="medium">$ {totalDue.toFixed(2)}</Typography>
                                </ContentGridSpaced>
                                <ContentGridSpaced>
                                    <Typography>Total Remaining</Typography>
                                    <Typography fontWeight="medium">$ {totalRemaining.toFixed(2)}</Typography>
                                </ContentGridSpaced>
                            </SummaryColumn>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <DetailsColumn>
                                <DetailsBox>
                                    <Typography variant="h6">
                                        Payment Details
                                    </Typography>
                                    {isEditing && (
                                        <Button
                                            startIcon={<Add />}
                                            onClick={handleAddPayment}
                                            size="small"
                                            variant="outlined"
                                        >
                                            Add Payment
                                        </Button>
                                    )}
                                </DetailsBox>
                                {localPayments.length > 0 ? (
                                    localPayments.map((payment, i) => {
                                        const locked = isPaymentLocked(payment);
                                        const isEditingThis = editingPaymentIndex === i;
                                        const canEdit = isEditing && !locked;
                                        const isNew = isNewPayment(payment, i);
                                        
                                        return (
                                            <PaymentItem key={i}>
                                                <ContentGridSpaced>
                                                    <Typography fontWeight="medium">Payment {payment.paymentNumber || i + 1}</Typography>
                                                    <PaymentAmountContainer>
                                                        {isEditingThis ? (
                                                            <StyledTextField
                                                                type="number"
                                                                size="small"
                                                                value={payment.value || ''}
                                                                onChange={(e) => handlePaymentChange(i, "value", e.target.value)}
                                                                inputProps={{ step: 1, min: 1 }}
                                                                error={!payment.value || parseFloat(payment.value) <= 0}
                                                                helperText={(!payment.value || parseFloat(payment.value) <= 0) ? "Must be greater than $0.00" : ""}
                                                                InputProps={{
                                                                    startAdornment: <InputAdornment position="start">$</InputAdornment>
                                                                }}
                                                            />
                                                        ) : (
                                                            <Typography fontWeight="medium">$ {parseFloat(payment.value || 0).toFixed(2)}</Typography>
                                                        )}
                                                        {canEdit && !isEditingThis && (
                                                            <IconButton size="small" onClick={() => handleStartEditPayment(i)}>
                                                                <Edit fontSize="small" />
                                                            </IconButton>
                                                        )}
                                                        {locked && (
                                                            <Tooltip title="Linked to an invoice — edit it from the invoice.">
                                                                <LockIndicator>
                                                                    <LockOutlined fontSize="small" />
                                                                </LockIndicator>
                                                            </Tooltip>
                                                        )}
                                                        {isEditingThis && (
                                                            <>
                                                                <IconButton size="small" onClick={() => handleSavePayment(i)}>
                                                                    <Check fontSize="small" />
                                                                </IconButton>
                                                                <IconButton size="small" onClick={handleCancelEditPayment}>
                                                                    <Close fontSize="small" />
                                                                </IconButton>
                                                            </>
                                                        )}
                                                    </PaymentAmountContainer>
                                                </ContentGridSpaced>
                                                <PaymentDetailsContainer>
                                                    {isEditingThis ? (
                                                        <>
                                                            <StyledFormControl size="small">
                                                                <InputLabel>Status</InputLabel>
                                                                <Select
                                                                    value={payment.status || "Pending"}
                                                                    label="Status"
                                                                    onChange={(e) => handlePaymentChange(i, "status", e.target.value)}
                                                                >
                                                                    <MenuItem value="Pending">Pending</MenuItem>
                                                                    {!isNew && <MenuItem value="Paid">Paid</MenuItem>}
                                                                    <MenuItem value="Due">Due</MenuItem>
                                                                </Select>
                                                            </StyledFormControl>
                                                            <StyledDateTextField>
                                                                <AppDatePicker
                                                                    label="Due Date"
                                                                    size="small"
                                                                    value={formatDay(payment.date)}
                                                                    onChange={(v) => handlePaymentChange(i, "date", v)}
                                                                />
                                                            </StyledDateTextField>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Chip label={payment.status || "Pending"} size="small" color={getStatusColor(payment.status || "Pending")} />
                                                            <Typography variant="body2" color="text.secondary">
                                                                Due: {formatDate(payment.date)}
                                                            </Typography>
                                                        </>
                                                    )}
                                                </PaymentDetailsContainer>
                                                <PaymentProgressContainer>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {payment.percentage ? payment.percentage.toFixed(1) : 0}% of total
                                                    </Typography>
                                                    <StyledLinearProgress
                                                        value={timePercentage(payment.value || 0, totalPayment || 1)}
                                                        variant="determinate"
                                                        color="primary"
                                                    />
                                                </PaymentProgressContainer>
                                            </PaymentItem>
                                        );
                                    })
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        No payments configured
                                    </Typography>
                                )}
                            </DetailsColumn>
                        </Grid>
                    </Grid>
                ) : (
                    <Box>
                        <Typography>No payments Configured</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Please configure payments for this project to see the progress.
                        </Typography>
                    </Box>
                )}
            </CardContent>
        </InfoCard>
    );
};

export default ProjectPayments;